import { PageLoader } from '@/components/ui/loader';
import { AiEmailWriter, AiRewriteMenu } from '@/features/ai/ai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BookOpenCheck,
  CalendarPlus,
  Copy,
  Eye,
  Mail,
  MessageCircle,
  MousePointerClick,
  Pause,
  Phone,
  PhoneCall,
  Play,
  Reply,
  Send,
  Square,
  Workflow,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Switch, Textarea } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  CALL_OUTCOMES,
  ENROLLMENT_STATUS,
  formatDuration,
  useCalls,
  useCreateMeeting,
  useEnroll,
  useEnrollmentAction,
  useEnrollments,
  useLogCall,
  useMailboxes,
  useMeetingLinks,
  useMeetings,
  useMergeFields,
  usePlaybooks,
  useRunPlaybook,
  useSendEmail,
  useSequences,
  useTemplates,
  useTrackedEmails,
  type CallOutcome,
  type Playbook,
  type RecordType,
} from './api';

const message = (error: unknown) => (error instanceof ApiRequestError ? error.message : 'Something went wrong — try again.');

type RecordRef = {
  entityType: RecordType;
  entityId: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  whatsapp?: string | null;
};

/** wa.me wants digits only, with the country code. */
const waNumber = (value: string) => value.replace(/[^\d]/g, '').replace(/^00/, '');

/**
 * The rep's tools on a record (HubSpot's record sidebar): email, call,
 * meeting, sequence and playbook — each one logs itself on the timeline.
 */
export function SalesToolbar({ record, variant = 'buttons' }: { record: RecordRef; variant?: 'buttons' | 'round' }) {
  const { can } = usePermission();
  const [open, setOpen] = useState<'email' | 'call' | 'meeting' | 'sequence' | 'playbook' | null>(null);
  const enrollable = record.entityType === 'lead' || record.entityType === 'contact';
  const whatsapp = record.whatsapp || record.phone;

  const tools = [
    can('crm.email.send') && { key: 'email' as const, label: 'Email', icon: Mail },
    can('crm.call.create') && { key: 'call' as const, label: 'Call', icon: Phone },
    whatsapp && { key: 'whatsapp' as const, label: 'WhatsApp', icon: MessageCircle },
    can('crm.meeting.create') && { key: 'meeting' as const, label: 'Meeting', icon: CalendarPlus },
    enrollable && can('crm.sequence.update') && { key: 'sequence' as const, label: 'Sequence', icon: Workflow },
    can('crm.playbook.view') && { key: 'playbook' as const, label: 'Playbook', icon: BookOpenCheck },
  ].filter(Boolean) as { key: 'email' | 'call' | 'whatsapp' | 'meeting' | 'sequence' | 'playbook'; label: string; icon: typeof Mail }[];

  const waHref = `https://wa.me/${waNumber(whatsapp || '')}`;

  return (
    <div className={variant === 'round' ? 'flex flex-wrap justify-start gap-x-3 gap-y-2' : 'flex flex-wrap gap-2'}>
      {tools.map((tool) =>
        variant === 'round' ? (
          tool.key === 'whatsapp' ? (
            <a key={tool.key} href={waHref} target="_blank" rel="noreferrer" className="group flex w-12 flex-col items-center gap-1 text-[11px] text-muted-foreground">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border bg-card text-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary">
                <tool.icon className="h-4 w-4" />
              </span>
              {tool.label}
            </a>
          ) : (
            <button key={tool.key} type="button" onClick={() => setOpen(tool.key as typeof open)} className="group flex w-12 flex-col items-center gap-1 text-[11px] text-muted-foreground">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border bg-card text-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary">
                <tool.icon className="h-4 w-4" />
              </span>
              {tool.label}
            </button>
          )
        ) : tool.key === 'whatsapp' ? (
          <Button key={tool.key} size="sm" variant="outline" asChild>
            <a href={waHref} target="_blank" rel="noreferrer">
              <tool.icon /> {tool.label}
            </a>
          </Button>
        ) : (
          <Button key={tool.key} size="sm" variant="outline" onClick={() => setOpen(tool.key as typeof open)}>
            <tool.icon /> {tool.label}
          </Button>
        ),
      )}

      {open === 'email' && <EmailComposer record={record} onClose={() => setOpen(null)} />}
      {open === 'call' && <CallDialog record={record} onClose={() => setOpen(null)} />}
      {open === 'meeting' && <MeetingDialog record={record} onClose={() => setOpen(null)} />}
      {open === 'sequence' && <EnrollDialog record={record} onClose={() => setOpen(null)} />}
      {open === 'playbook' && <PlaybookDialog record={record} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ── Email ────────────────────────────────────────────────────────────────────

export function EmailComposer({ record, onClose }: { record: RecordRef; onClose: () => void }) {
  const send = useSendEmail();
  const { data: mailboxes = [] } = useMailboxes();
  const { data: templates = [] } = useTemplates();
  const { data: fields = [] } = useMergeFields();
  const [channelId, setChannelId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [to, setTo] = useState(record.email ?? '');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [track, setTrack] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const emailTemplates = templates.filter((template) => template.kind === 'TEMPLATE');
  const snippets = templates.filter((template) => template.kind === 'SNIPPET');

  useEffect(() => {
    if (mailboxes.length && !channelId) setChannelId(mailboxes[0].id);
  }, [mailboxes, channelId]);

  const insert = (text: string) => {
    const area = bodyRef.current;
    if (!area) return setBody((current) => current + text);
    const start = area.selectionStart ?? body.length;
    const end = area.selectionEnd ?? body.length;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(start + text.length, start + text.length);
    });
  };

  /** "#price " typed in the body becomes that snippet. */
  const expandSnippets = (value: string) => {
    const match = value.match(/(^|\s)#([a-z0-9_-]+)\s$/i);
    if (!match) return value;
    const snippet = snippets.find((item) => item.shortcut === match[2].toLowerCase());
    if (!snippet) return value;
    return value.slice(0, value.length - match[2].length - 2) + snippet.body + ' ';
  };

  const pickTemplate = (id: string) => {
    setTemplateId(id);
    const template = emailTemplates.find((item) => item.id === id);
    if (template) {
      setSubject(template.subject ?? '');
      setBody(template.body);
    }
  };

  const submit = async () => {
    setError(null);
    try {
      await send.mutateAsync({
        entityType: record.entityType,
        entityId: record.entityId,
        to: to.trim() || undefined,
        subject,
        body,
        templateId: templateId || undefined,
        channelId: channelId || undefined,
        track,
      });
      toast.success('Email sent');
      onClose();
    } catch (failure) {
      setError(message(failure));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Email {record.name}</DialogTitle>
          <DialogDescription>
            Logged on the timeline. Opens and clicks are tracked; a reply into a connected mailbox stops any sequence.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>To</Label>
              <Input value={to} onChange={(event) => setTo(event.target.value)} placeholder="name@company.ae" />
            </div>
            <div className="space-y-1.5">
              <Label>From</Label>
              <Select value={channelId} onChange={(event) => setChannelId(event.target.value)}>
                {mailboxes.map((box) => (
                  <option key={box.id} value={box.id}>
                    {box.name}
                    {box.address ? ` — ${box.address}` : ''}
                  </option>
                ))}
                <option value="">DovoCRM mail (replies come to your own email)</option>
              </Select>
            </div>
          </div>
          {emailTemplates.length > 0 && (
            <div className="space-y-1.5">
              <Label>Template</Label>
              <Select value={templateId} onChange={(event) => pickTemplate(event.target.value)}>
                <option value="">Start from blank</option>
                {emailTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.folder ? `${template.folder} / ` : ''}
                    {template.name}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <AiEmailWriter
            entityType={record.entityType}
            entityId={record.entityId}
            onDraft={(draft) => {
              if (draft.subject) setSubject(draft.subject);
              setBody(draft.body);
            }}
          />
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={subject} onChange={(event) => setSubject(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Message</Label>
              <div className="flex flex-wrap gap-1.5">
                <AiRewriteMenu text={body} onResult={setBody} />
                <Select
                  className="h-8 w-auto text-xs"
                  value=""
                  onChange={(event) => event.target.value && insert(`{{${event.target.value}}}`)}
                >
                  <option value="">Insert field…</option>
                  {fields.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </Select>
                {snippets.length > 0 && (
                  <Select
                    className="h-8 w-auto text-xs"
                    value=""
                    onChange={(event) => {
                      const snippet = snippets.find((item) => item.id === event.target.value);
                      if (snippet) insert(snippet.body);
                    }}
                  >
                    <option value="">Insert snippet…</option>
                    {snippets.map((snippet) => (
                      <option key={snippet.id} value={snippet.id}>
                        {snippet.shortcut ? `#${snippet.shortcut} — ` : ''}
                        {snippet.name}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            </div>
            <Textarea
              ref={bodyRef}
              rows={10}
              value={body}
              onChange={(event) => setBody(expandSnippets(event.target.value))}
              placeholder={'Hi {{firstName|there}},\n\n…\n\n{{sender.name}}'}
            />
            <p className="text-xs text-muted-foreground">
              Placeholders like {'{{firstName}}'} are filled from the record. Type #shortcut and a space for a snippet.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={track} onCheckedChange={setTrack} /> Track opens and clicks
          </label>
          <FormError message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={send.isPending || !subject.trim() || !body.trim()}>
            <Send /> {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Calls ────────────────────────────────────────────────────────────────────

function CallDialog({ record, onClose }: { record: RecordRef; onClose: () => void }) {
  const log = useLogCall();
  const [phone, setPhone] = useState(record.phone ?? record.whatsapp ?? '');
  const [direction, setDirection] = useState<'OUTBOUND' | 'INBOUND'>('OUTBOUND');
  const [outcome, setOutcome] = useState<CallOutcome>('CONNECTED');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [minutes, setMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [followTitle, setFollowTitle] = useState(`Follow up with ${record.name}`);
  const [followAt, setFollowAt] = useState(() => {
    const date = new Date(Date.now() + 86_400_000);
    date.setHours(10, 0, 0, 0);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!startedAt) return;
    const timer = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);

  const dial = () => {
    setStartedAt(Date.now());
    setElapsed(0);
  };

  const seconds = startedAt ? elapsed : Math.round(Number(minutes || 0) * 60);

  const submit = async () => {
    setError(null);
    try {
      await log.mutateAsync({
        entityType: record.entityType,
        entityId: record.entityId,
        phone,
        direction,
        outcome,
        durationSeconds: seconds,
        notes,
        ...(startedAt ? { startedAt: new Date(startedAt).toISOString() } : {}),
        ...(followUp ? { followUp: { title: followTitle, dueAt: new Date(followAt).toISOString() } } : {}),
      });
      toast.success('Call logged');
      onClose();
    } catch (failure) {
      setError(message(failure));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Call {record.name}</DialogTitle>
          <DialogDescription>Dial from your phone, then log how it went. The timer runs while you talk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+971 50 123 4567" />
            <Button asChild disabled={!phone} onClick={dial}>
              <a href={phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : undefined}>
                <PhoneCall /> Dial
              </a>
            </Button>
          </div>
          {startedAt && (
            <div className="flex items-center justify-between rounded-lg bg-primary/5 px-3 py-2 text-sm">
              <span className="font-medium text-primary">On the call · {formatDuration(elapsed)}</span>
              <Button size="sm" variant="ghost" onClick={() => { setMinutes(String(Math.round(elapsed / 6) / 10)); setStartedAt(null); }}>
                <Square /> End
              </Button>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Direction</Label>
              <Select value={direction} onChange={(event) => setDirection(event.target.value as 'OUTBOUND' | 'INBOUND')}>
                <option value="OUTBOUND">Outgoing</option>
                <option value="INBOUND">Incoming</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Outcome</Label>
              <Select value={outcome} onChange={(event) => setOutcome(event.target.value as CallOutcome)}>
                {CALL_OUTCOMES.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Minutes</Label>
              <Input
                type="number"
                min={0}
                step="0.5"
                value={startedAt ? String(Math.round(elapsed / 6) / 10) : minutes}
                disabled={Boolean(startedAt)}
                onChange={(event) => setMinutes(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="What was discussed, what happens next" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={followUp} onCheckedChange={(value) => setFollowUp(Boolean(value))} /> Book a follow-up task
          </label>
          {followUp && (
            <div className="grid gap-2 sm:grid-cols-[1fr_12rem]">
              <Input value={followTitle} onChange={(event) => setFollowTitle(event.target.value)} />
              <Input type="datetime-local" value={followAt} onChange={(event) => setFollowAt(event.target.value)} />
            </div>
          )}
          <FormError message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={log.isPending}>
            {log.isPending ? 'Saving…' : 'Log call'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Meetings ─────────────────────────────────────────────────────────────────

function MeetingDialog({ record, onClose }: { record: RecordRef; onClose: () => void }) {
  const create = useCreateMeeting();
  const { data: links = [] } = useMeetingLinks();
  const [title, setTitle] = useState(`Meeting with ${record.name}`);
  const [date, setDate] = useState(() => {
    const day = new Date(Date.now() + 86_400_000);
    day.setHours(11, 0, 0, 0);
    return new Date(day.getTime() - day.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  });
  const [duration, setDuration] = useState(30);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [sendInvite, setSendInvite] = useState(Boolean(record.email));
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    const start = new Date(date);
    try {
      await create.mutateAsync({
        title,
        startsAt: start.toISOString(),
        endsAt: new Date(start.getTime() + duration * 60_000).toISOString(),
        location: location || null,
        notes: notes || null,
        entityType: record.entityType,
        entityId: record.entityId,
        sendInvite,
      });
      toast.success('Meeting scheduled');
      onClose();
    } catch (failure) {
      setError(message(failure));
    }
  };

  const copy = async (url: string) => {
    await navigator.clipboard.writeText(url).catch(() => undefined);
    toast.success('Booking link copied — paste it into your message');
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Meeting with {record.name}</DialogTitle>
          <DialogDescription>Schedule it yourself, or send your booking link and let them pick a time.</DialogDescription>
        </DialogHeader>
        {links.length > 0 && (
          <div className="space-y-1.5 rounded-lg border bg-muted/30 p-3">
            <p className="text-xs font-medium text-muted-foreground">Booking links</p>
            {links
              .filter((link) => link.isActive)
              .map((link) => (
                <div key={link.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate">
                    {link.name} · {link.durationMinutes} min
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => copy(link.url)}>
                    <Copy /> Copy link
                  </Button>
                </div>
              ))}
          </div>
        )}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Starts</Label>
              <Input type="datetime-local" value={date} onChange={(event) => setDate(event.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Length</Label>
              <Select value={duration} onChange={(event) => setDuration(Number(event.target.value))}>
                {[15, 30, 45, 60, 90, 120].map((value) => (
                  <option key={value} value={value}>
                    {value} minutes
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Where</Label>
            <Input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Office, Google Meet link, Zoom…" />
          </div>
          <div className="space-y-1.5">
            <Label>Agenda / notes</Label>
            <Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </div>
          {record.email && (
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={sendInvite} onCheckedChange={setSendInvite} /> Email {record.email} a calendar invite
            </label>
          )}
          <FormError message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={create.isPending || title.trim().length < 2}>
            {create.isPending ? 'Saving…' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Sequences ────────────────────────────────────────────────────────────────

export function EnrollDialog({
  record,
  targets,
  onClose,
}: {
  record?: RecordRef;
  /** Several at once, from a list's bulk actions. */
  targets?: { entityType: 'lead' | 'contact'; entityId: string }[];
  onClose: () => void;
}) {
  const enroll = useEnroll();
  const { data: sequences = [] } = useSequences();
  const active = sequences.filter((sequence) => sequence.isActive);
  const [sequenceId, setSequenceId] = useState('');
  const [later, setLater] = useState(false);
  const [startAt, setStartAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = targets ?? (record ? [{ entityType: record.entityType as 'lead' | 'contact', entityId: record.entityId }] : []);

  useEffect(() => {
    if (active.length && !sequenceId) setSequenceId(active[0].id);
  }, [active, sequenceId]);

  const chosen = active.find((sequence) => sequence.id === sequenceId);

  const submit = async () => {
    setError(null);
    try {
      const result = await enroll.mutateAsync({
        sequenceId,
        targets: list,
        ...(later && startAt ? { startAt: new Date(startAt).toISOString() } : {}),
      });
      if (result.enrolled.length) toast.success(`${result.enrolled.length} enrolled in "${chosen?.name}"`);
      if (result.skipped.length) {
        const reasons = [...new Set(result.skipped.map((item) => item.reason))].join(', ');
        (result.enrolled.length ? toast.warning : toast.error)(`${result.skipped.length} skipped — ${reasons}`);
      }
      if (result.enrolled.length) onClose();
    } catch (failure) {
      setError(message(failure));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enrol {record ? record.name : `${list.length} people`} in a sequence</DialogTitle>
          <DialogDescription>
            Emails go out as you, on working days and hours. A reply or a booked meeting takes them out automatically.
          </DialogDescription>
        </DialogHeader>
        {active.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No active sequences yet. <Link className="text-primary underline" to="/sales/sequences">Create one</Link>.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Sequence</Label>
              <Select value={sequenceId} onChange={(event) => setSequenceId(event.target.value)}>
                {active.map((sequence) => (
                  <option key={sequence.id} value={sequence.id}>
                    {sequence.name} · {sequence.stepCount} steps
                  </option>
                ))}
              </Select>
            </div>
            {chosen && (
              <ol className="space-y-1 rounded-lg border bg-muted/30 p-3 text-xs">
                {chosen.steps.map((step, index) => (
                  <li key={step.id} className="flex gap-2">
                    <span className="w-14 shrink-0 text-muted-foreground">Day {chosen.steps.slice(0, index + 1).reduce((sum, item) => sum + item.delayDays, 0)}</span>
                    <span>{step.type === 'email' ? `Email: ${step.subject || '(same thread)'}` : `${step.type === 'call' ? 'Call' : 'Task'}: ${step.title}`}</span>
                  </li>
                ))}
              </ol>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={later} onCheckedChange={setLater} /> Start later
            </label>
            {later && <Input type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} />}
          </div>
        )}
        <FormError message={error} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!sequenceId || enroll.isPending || (later && !startAt)}>
            {enroll.isPending ? 'Enrolling…' : 'Enrol'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Playbooks ────────────────────────────────────────────────────────────────

function PlaybookDialog({ record, onClose }: { record: RecordRef; onClose: () => void }) {
  const { data: playbooks = [] } = usePlaybooks(record.entityType);
  const run = useRunPlaybook();
  const [chosen, setChosen] = useState<Playbook | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!chosen && playbooks.length === 1) setChosen(playbooks[0]);
  }, [playbooks, chosen]);

  const set = (id: string, value: unknown) => setAnswers((current) => ({ ...current, [id]: value }));

  const submit = async () => {
    if (!chosen) return;
    setError(null);
    try {
      await run.mutateAsync({ id: chosen.id, entityType: record.entityType, entityId: record.entityId, answers });
      toast.success('Saved to the timeline');
      onClose();
    } catch (failure) {
      setError(message(failure));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{chosen ? chosen.name : 'Choose a playbook'}</DialogTitle>
          <DialogDescription>{chosen?.description ?? 'The questions to work through with the customer.'}</DialogDescription>
        </DialogHeader>
        {!chosen ? (
          playbooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No playbooks for this kind of record. <Link className="text-primary underline" to="/sales/playbooks">Create one</Link>.
            </p>
          ) : (
            <div className="space-y-2">
              {playbooks.map((playbook) => (
                <button
                  key={playbook.id}
                  onClick={() => setChosen(playbook)}
                  className="w-full rounded-lg border p-3 text-start transition-colors hover:border-primary/40 hover:bg-accent"
                >
                  <p className="font-medium">{playbook.name}</p>
                  <p className="text-xs text-muted-foreground">{playbook.questions.length} questions</p>
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-4">
            {chosen.questions.map((question, index) => (
              <div key={question.id} className="space-y-1.5">
                <Label>
                  {index + 1}. {question.label}
                  {question.required && <span className="text-destructive"> *</span>}
                </Label>
                {question.help && <p className="text-xs text-muted-foreground">{question.help}</p>}
                {question.type === 'text' && <Input value={String(answers[question.id] ?? '')} onChange={(event) => set(question.id, event.target.value)} />}
                {question.type === 'number' && (
                  <Input type="number" value={String(answers[question.id] ?? '')} onChange={(event) => set(question.id, event.target.value === '' ? '' : Number(event.target.value))} />
                )}
                {question.type === 'textarea' && (
                  <Textarea rows={3} value={String(answers[question.id] ?? '')} onChange={(event) => set(question.id, event.target.value)} />
                )}
                {question.type === 'checkbox' && (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={Boolean(answers[question.id])} onCheckedChange={(value) => set(question.id, Boolean(value))} /> Yes
                  </label>
                )}
                {question.type === 'select' && (
                  <Select value={String(answers[question.id] ?? '')} onChange={(event) => set(question.id, event.target.value)}>
                    <option value="">Choose…</option>
                    {question.options?.map((option) => (
                      <option key={option}>{option}</option>
                    ))}
                  </Select>
                )}
                {question.type === 'multiselect' && (
                  <div className="flex flex-wrap gap-1.5">
                    {question.options?.map((option) => {
                      const picked = ((answers[question.id] as string[]) ?? []).includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => {
                            const current = (answers[question.id] as string[]) ?? [];
                            set(question.id, picked ? current.filter((item) => item !== option) : [...current, option]);
                          }}
                          className={cn(
                            'rounded-full border px-3 py-1 text-xs',
                            picked ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent',
                          )}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            <FormError message={error} />
          </div>
        )}
        <DialogFooter>
          {chosen && playbooks.length > 1 && (
            <Button variant="ghost" onClick={() => setChosen(null)}>
              Another playbook
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {chosen && (
            <Button onClick={submit} disabled={run.isPending}>
              {run.isPending ? 'Saving…' : 'Save answers'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── What has happened on the record ──────────────────────────────────────────

/** Tracked emails, calls, meetings and sequences for one record. */
export function RecordSalesActivity({ entityType, entityId }: { entityType: RecordType; entityId: string }) {
  const { can } = usePermission();
  const [tab, setTab] = useState<'emails' | 'calls' | 'meetings' | 'sequences'>('emails');
  const tabs = useMemo(
    () =>
      (
        [
          ['emails', 'Emails', 'crm.email.view'],
          ['calls', 'Calls', 'crm.call.view'],
          ['meetings', 'Meetings', 'crm.meeting.view'],
          ['sequences', 'Sequences', 'crm.sequence.view'],
        ] as const
      ).filter(([key, , permission]) => can(permission) && (key !== 'sequences' || entityType === 'lead' || entityType === 'contact')),
    [can, entityType],
  );
  if (!tabs.length) return null;

  return (
    <div className="rounded-2xl border bg-card">
      <div className="flex gap-1 border-b px-3 pt-2">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === key ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="p-4">
        {tab === 'emails' && <EmailsList entityType={entityType} entityId={entityId} />}
        {tab === 'calls' && <CallsList entityType={entityType} entityId={entityId} />}
        {tab === 'meetings' && <MeetingsList entityType={entityType} entityId={entityId} />}
        {tab === 'sequences' && <EnrollmentsList entityType={entityType} entityId={entityId} />}
      </div>
    </div>
  );
}

export function EmailStatus({ email }: { email: { status: string; openCount: number; clickCount: number; repliedAt: string | null; error?: string | null } }) {
  if (email.status === 'FAILED') return <Badge className="bg-red-100 text-red-700">Failed</Badge>;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs">
      {email.repliedAt && (
        <Badge className="bg-emerald-100 text-emerald-700">
          <Reply className="me-1 h-3 w-3" /> Replied
        </Badge>
      )}
      {email.openCount > 0 ? (
        <Badge className="bg-blue-100 text-blue-700">
          <Eye className="me-1 h-3 w-3" /> Opened {email.openCount > 1 ? `×${email.openCount}` : ''}
        </Badge>
      ) : (
        <Badge className="bg-neutral-100 text-neutral-600">Not opened</Badge>
      )}
      {email.clickCount > 0 && (
        <Badge className="bg-violet-100 text-violet-700">
          <MousePointerClick className="me-1 h-3 w-3" /> Clicked
        </Badge>
      )}
    </span>
  );
}

function EmailsList({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data, isLoading } = useTrackedEmails({ entityType, entityId });
  const [open, setOpen] = useState<string | null>(null);
  if (isLoading) return <PageLoader />;
  if (!data?.items.length) return <p className="text-sm text-muted-foreground">No emails sent from the CRM yet.</p>;
  return (
    <ul className="divide-y">
      {data.items.map((email) => (
        <li key={email.id} className="py-2.5">
          <button className="w-full text-start" onClick={() => setOpen(open === email.id ? null : email.id)}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">{email.subject}</span>
              <EmailStatus email={email} />
            </div>
            <p className="text-xs text-muted-foreground">
              To {email.to} · {email.sentBy?.name ?? 'Sequence'} · {formatDateTime(email.sentAt)}
              {email.enrollmentId ? ' · from a sequence' : ''}
            </p>
          </button>
          {open === email.id && (
            <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-muted/40 p-3 font-sans text-sm">{email.error ?? email.body}</pre>
          )}
        </li>
      ))}
    </ul>
  );
}

function CallsList({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data: calls = [], isLoading } = useCalls({ entityType, entityId });
  if (isLoading) return <PageLoader />;
  if (!calls.length) return <p className="text-sm text-muted-foreground">No calls logged yet.</p>;
  return (
    <ul className="divide-y">
      {calls.map((call) => (
        <li key={call.id} className="py-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">
              {call.direction === 'INBOUND' ? 'Incoming' : 'Outgoing'} · {call.outcomeLabel}
            </span>
            <span className="text-xs text-muted-foreground">{formatDuration(call.durationSeconds)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {call.user?.name} · {call.phone ?? ''} · {formatDateTime(call.startedAt)}
          </p>
          {call.notes && <p className="mt-1 whitespace-pre-wrap text-sm">{call.notes}</p>}
          {call.recordingUrl && (
            <a className="text-xs text-primary underline" href={call.recordingUrl} target="_blank" rel="noreferrer">
              Recording
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

function MeetingsList({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data: meetings = [], isLoading } = useMeetings({ entityType, entityId });
  if (isLoading) return <PageLoader />;
  if (!meetings.length) return <p className="text-sm text-muted-foreground">No meetings yet.</p>;
  return (
    <ul className="divide-y">
      {meetings.map((meeting) => (
        <li key={meeting.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
          <div>
            <p className="font-medium">{meeting.title}</p>
            <p className="text-xs text-muted-foreground">
              {formatDateTime(meeting.startsAt)} · {meeting.host?.name}
              {meeting.location ? ` · ${meeting.location}` : ''}
            </p>
          </div>
          <MeetingStatus status={meeting.status} />
        </li>
      ))}
    </ul>
  );
}

export function MeetingStatus({ status }: { status: string }) {
  const map: Record<string, [string, string]> = {
    SCHEDULED: ['Scheduled', 'bg-blue-100 text-blue-700'],
    COMPLETED: ['Held', 'bg-emerald-100 text-emerald-700'],
    CANCELLED: ['Cancelled', 'bg-neutral-200 text-neutral-600'],
    NO_SHOW: ['No-show', 'bg-amber-100 text-amber-700'],
  };
  const [label, className] = map[status] ?? [status, ''];
  return <Badge className={className}>{label}</Badge>;
}

function EnrollmentsList({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { data: rows = [], isLoading } = useEnrollments({ entityType, entityId });
  const act = useEnrollmentAction();
  if (isLoading) return <PageLoader />;
  if (!rows.length) return <p className="text-sm text-muted-foreground">Not in any sequence.</p>;
  return (
    <ul className="divide-y">
      {rows.map((row) => (
        <EnrollmentRow key={row.id} row={row} onAct={(action) => act.mutate({ id: row.id, action })} />
      ))}
    </ul>
  );
}

export function EnrollmentRow({
  row,
  onAct,
  showName = false,
}: {
  row: import('./api').Enrollment;
  onAct: (action: 'pause' | 'resume' | 'stop') => void;
  showName?: boolean;
}) {
  const status = ENROLLMENT_STATUS[row.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5">
      <div className="min-w-0">
        <p className="font-medium">
          {showName ? (
            <Link className="hover:underline" to={`/crm/${row.entityType === 'lead' ? 'leads' : 'contacts'}/${row.entityId}`}>
              {row.name || row.email}
            </Link>
          ) : (
            row.sequence.name
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {showName ? `${row.email} · ` : ''}Step {Math.min(row.currentStep + 1, row.stepCount)} of {row.stepCount} · {row.emailsSent} sent
          {row.opened ? ' · opened' : ''}
          {row.clicked ? ' · clicked' : ''}
          {row.nextRunAt && row.status === 'ACTIVE' ? ` · next ${formatDateTime(row.nextRunAt)}` : ''}
          {row.stoppedReason ? ` · ${row.stoppedReason}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        <Badge className={status.className}>{status.label}</Badge>
        {row.status === 'ACTIVE' && (
          <Button size="sm" variant="ghost" title="Pause" onClick={() => onAct('pause')}>
            <Pause />
          </Button>
        )}
        {row.status === 'PAUSED' && (
          <Button size="sm" variant="ghost" title="Resume" onClick={() => onAct('resume')}>
            <Play />
          </Button>
        )}
        {(row.status === 'ACTIVE' || row.status === 'PAUSED') && (
          <Button size="sm" variant="ghost" title="Stop" onClick={() => onAct('stop')}>
            <Square />
          </Button>
        )}
      </div>
    </li>
  );
}
